// export interface IUser {
//     username: string;
//     empresa: string;
//     obra: string;
//     roles: string;
//     email: string;
//     email_verified: boolean;
//     name: string;
// }
import type { PapelLegado } from "@/shared/autorizacao/catalogo/legado";
import type { NomePapel } from "@/shared/autorizacao/catalogo/vocabulario";
import type { Delegacao } from "@/shared/autorizacao/delegacao";

/** Payload bruto de origem heterogenea (Cognito, Dynamo, default, federado). */
type PayloadUsuario = Record<string, unknown>;

export default class User {
  username: string = "";
  empresa: string[] = [];
  obra: string[] = [];
  roles: string[] = [];
  email: string = "";
  email_verified: boolean = false;
  name: string = "";
  id_user: string = "";
  pk: string = "";
  sk: string = "";
  specific_rules: object = {};
  tipo: string = "";
  /**
   * Delegações RECEBIDAS por este usuário, na forma que
   * `docs/contratos-backend.md` §9 (BE-11) declara. Passthrough puro do
   * payload — `undefined` quando ausente, nunca `[]` forjado — porque a
   * origem é o backend, e o backend ainda não entrega este campo em NENHUMA
   * forma de payload (BE-11 continua 🟥). Lido igual nas quatro formas
   * (T158): é o único campo deste payload que não é aninhado nem
   * serializado como as outras (`custom:*` do Cognito viram JSON em string;
   * `delegacoes` já chega como array, pelo tipo que o próprio contrato
   * declara), então uma leitura no topo do construtor cobre as quatro
   * ramificações sem duplicar a linha em cada uma.
   */
  delegacoes?: readonly Delegacao[];
  constructor(user: PayloadUsuario) {
    this.delegacoes = user.delegacoes as Delegacao[] | undefined;

    if (user.attributes || user.challengeParam) {
      this.preencheUserCognito(user);
      return;
    }
    if (user.PK) {
      this.preencheUserDynamo(user);
      return;
    }
    if (user.id_user || user.pk) {
      this.preencheUserDefault(user);
      return;
    }

    if (Object.keys(user).length) this.preencheUserFederado(user);
  }

  private preencheUserDynamo(user: PayloadUsuario) {
    this.pk = user.PK as string;
    this.sk = user.SK as string;
    this.username = user.username as string;
    this.empresa = user.empresa as string[];
    this.obra = user.obra as string[];
    this.roles = user.roles as string[];
    this.email = user.mail as string;
    this.email_verified = user.email_verified as boolean;
    this.name = user.displayName as string;
    this.specific_rules = user.specific_rules as object;
    this.tipo = "gali";
  }

  private preencheUserDefault(user: PayloadUsuario) {
    this.pk = user.pk as string;
    this.sk = user.sk as string;
    this.username = user.username as string;
    this.empresa = this.testaJSON(user.empresa as string | string[]);
    this.obra = this.testaJSON((user.obra as string | string[]) || []);
    this.roles = this.testaJSON(user.roles as string | string[]);
    this.email = user.email as string;
    this.email_verified = user.email_verified as boolean;
    this.name = user.name as string;
    this.specific_rules = user.specific_rules as object;
    this.tipo = user.tipo as string;
  }

  private preencheUserCognito(user: PayloadUsuario) {
    if (user.challengeParam) {
      user.attributes = (user.challengeParam as { userAttributes: PayloadUsuario }).userAttributes;
    }
    const atributos = user.attributes as PayloadUsuario;
    this.pk = "";
    this.sk = "";

    this.username = user.username as string;
    this.email = atributos["email"] as string;
    this.id_user = this.email;
    this.email_verified = atributos.email_verified as boolean;
    this.name = atributos.name as string;

    this.empresa = this.testaJSON(atributos["custom:empresa"] as string);
    this.obra = this.testaJSON(atributos["custom:obra"] as string);
    this.roles = this.testaJSON(atributos["custom:roles"] as string);
    this.specific_rules = this.testaJSON(atributos["custom:specific_rules"] as string);
    this.tipo = "cliente";
  }

  private preencheUserFederado(user: PayloadUsuario) {
    this.pk = "";
    this.sk = "";
    this.username = user["cognito:username"] as string;
    this.empresa = ["GALI"];
    this.obra = this.testaJSON(user["custom:obra"] as string);
    this.roles = this.testaJSON(user["custom:roles"] as string);
    const identidades = user.identities as Array<{ userId: string }> | undefined;
    this.email = identidades ? identidades[0]["userId"] : "";
    this.email_verified = true;
    this.name = user.name as string;
    this.id_user = user.sub as string;
    this.tipo = "gali";
  }

  public get todasPermissoes() {
    let permissõesDoUsuario = [...this.roles];
    permissõesDoUsuario.push(this.email);
    if (this.empresa?.length) permissõesDoUsuario = permissõesDoUsuario.concat(this.empresa);
    return permissõesDoUsuario;
  }

  /**
   * Um dos papéis listados está em `roles`.
   *
   * A lista é tipada (T46, PAPEL-04): `string[]` deixava inventar papel na chamada
   * e o erro só aparecia em runtime, como permissão que nunca concede. Agora os ~95
   * call sites são verificados pelo compilador.
   *
   * O tipo aceita o nome canônico (`NomePapel`) **e** as 52 chaves legadas
   * (`PapelLegado`), porque `roles` ainda carrega os nomes antigos: `custom:roles` e
   * o Dynamo não migraram, e `normalizarPapeis` (T44) ainda não está ligada na
   * fronteira. Tipar só com `NomePapel` reprovaria o kernel inteiro e, pior, casaria
   * contra nada em runtime. O conjunto aceito encolhe sozinho conforme cada tarefa
   * apaga um nome de `LEGADO`.
   */
  temPermissao(permissoes: (NomePapel | PapelLegado)[]) {
    return permissoes.some((permissao) => {
      return this.roles.includes(permissao);
    });
  }
  testaJSON(json: object | string | undefined | null) {
    if (json == null) return [];
    if (typeof json != "string") return json;
    try {
      return JSON.parse(json);
    } catch {
      return [];
    }
  }
}
